import { ModalScreens } from 'components/Navigation/types';
import { filterArrayEmpty } from 'lib/riskbase';
import { isError, noop, sortBy } from 'lodash';
import { getAssetFileInline, getAssetThumbnailInline } from 'service/storage';
import {
  ALERT,
  ALERT_ERROR,
  CATCH_ERROR,
  eventPinSelected,
  HIDE_BLOCKING_LOADER,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  SET_ASSET_DELTA,
  SET_GENERIC,
  SHOW_BLOCKING_LOADER,
} from 'store/actions';
import { selectGeneric } from 'store/app/selectors';
import { call, put, select, takeEvery } from 'typed-redux-saga';

import { AppContextEnum, appContextToFileContext } from 'types/types';
import { API_FILE_GENERIC } from 'service/api/constants';
import { getFilePreviewUri } from 'store/sagaHelpers';
import delay from 'delay';
import * as actions from './actions';
import {
  selectAfterActionFind,
  selectPlansPlan,
  selectPlansAssetId,
  selectPlansPlanId,
  selectPlansPin,
  selectPlansPlanPins,
  selectPlans,
  selectPlansSelectedPinId,
  selectSelectionKey,
  selectPlansContext,
} from './selectors';
import { GENERIC_OPEN_PLAN_ID, PlanLoaded } from './types';

const loadPlan = function* f(planId: string): Generator {
  try {
    const context = yield* select(selectPlansContext);
    const plan = (yield* select(selectPlans))[planId];

    if (!plan) {
      throw new Error('Plan not found');
    }

    yield* put(SHOW_BLOCKING_LOADER());

    const image: PlanLoaded['image'] = {
      base64: '',
      url: '',
      page: 0,
      isFileMissing: false,
      isUsingThumbnail: false,
      width: plan.fileWidth || 0,
      height: plan.fileHeight || 0,
      type: plan.fileType || 'image',
    };

    if (context.context === AppContextEnum.remoteAssessment) {
      try {
        switch (plan.fileType) {
          case 'svgpdf':
            image.page = 1;
            image.url = API_FILE_GENERIC(
              (yield* getFilePreviewUri(
                plan.fileId,
                appContextToFileContext(context),
                { convert: 'svgpdf', page: null },
              )).filename,
            );
            break;
          case 'pdfpage':
            image.page = 1;
            image.url = API_FILE_GENERIC(
              (yield* getFilePreviewUri(
                plan.fileId,
                appContextToFileContext(context),
                { convert: 'pdfpage', page: plan.filePage || 1 },
              )).filename,
            );
            break;
          case 'pdfimage':
            image.url = API_FILE_GENERIC(
              (yield* getFilePreviewUri(
                plan.fileId,
                appContextToFileContext(context),
                { convert: 'pdfimage', page: plan.filePage || 1 },
              )).filename,
            );
            break;
          case 'svg':
          case 'image':
          default:
            image.url = API_FILE_GENERIC(
              (yield* getFilePreviewUri(
                plan.fileId,
                appContextToFileContext(context),
                { convert: plan.fileType, page: plan.filePage || 1 },
              )).filename,
            );
            break;
        }
      } catch (_) {
        image.isFileMissing = true;
      }
    } else if (plan.fileType === 'pdf') {
      try {
        image.page = plan.filePage || 1;
        image.base64 = yield* call(
          getAssetFileInline,
          context.assetId,
          plan.fileId,
          'data:application/pdf;base64,',
        );
      } catch (_) {
        image.isFileMissing = true;
      }
    } else if (plan.fileType === 'svg') {
      try {
        image.page = plan.filePage || 1;
        image.base64 = yield* call(
          getAssetFileInline,
          context.assetId,
          plan.fileId,
          'data:image/svg+xml;base64,',
        );
      } catch (_) {
        image.isFileMissing = true;
      }
    } else if (plan.fileType === 'svgpdf') {
      try {
        image.page = 1;
        image.base64 = yield* call(
          getAssetFileInline,
          context.assetId,
          `${plan.fileId}:svgpdf`,
          'data:application/pdf;base64,',
        );
      } catch (_) {
        image.isFileMissing = true;
      }
    } else if (plan.fileType === 'pdfpage') {
      try {
        image.page = 1;
        image.base64 = yield* call(
          getAssetFileInline,
          context.assetId,
          `${plan.fileId}:pdfpage:${plan.filePage}`,
          'data:application/pdf;base64,',
        );
      } catch (_) {
        image.isFileMissing = true;
      }
    } else if (plan.fileType === 'pdfimage') {
      const filename = `${plan.fileId}:pdfimage:${plan.filePage}`;
      try {
        image.base64 = yield* call(
          getAssetFileInline,
          context.assetId,
          filename,
          'data:image/jpeg;base64,',
        );
      } catch (_) {
        noop();
      }
      // If image doesn't exist, attempt getting the thumbnail version – not so good
      if (!image.base64) {
        try {
          image.base64 = yield* call(
            getAssetThumbnailInline,
            context.assetId,
            `${plan.fileId}:${plan.filePage}`,
          );
          image.isUsingThumbnail = true;
        } catch (_) {
          image.isFileMissing = true;
          noop();
        }
      }
    } else {
      const filename = plan.fileId + (plan.filePage ? `:${plan.filePage}` : '');
      // Attempt to get large image - we'll get an error if it isn't downloaded
      try {
        image.base64 = yield* call(
          getAssetFileInline,
          context.assetId,
          filename,
          'data:image/jpeg;base64,',
        );
      } catch (_) {
        noop();
      }
      // If image doesn't exist, attempt getting the thumbnail version – not so good
      if (!image.base64) {
        try {
          image.base64 = yield* call(
            getAssetThumbnailInline,
            context.assetId,
            filename,
          );
          image.isUsingThumbnail = true;
        } catch (_) {
          image.isFileMissing = true;
          noop();
        }
      }
    }

    yield* put(
      SET_GENERIC({
        key: GENERIC_OPEN_PLAN_ID(context.assetId),
        value: plan.id,
      }),
    );

    yield* put(
      actions.doPlansSetPlan({
        plan: {
          id: plan.id,
          name: plan.name,
          image,
          pinCount: plan.pinCount,
        },
      }),
    );

    yield* put(HIDE_BLOCKING_LOADER());
    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.PLANS,
        params: { show: true },
      }),
    );

    if (image.isFileMissing) {
      yield delay(100);
      yield* put(
        ALERT({
          text: "This plan's file is missing. Trying closing plans and syncing",
        }),
      );
    }
  } catch (error) {
    yield* put(HIDE_BLOCKING_LOADER());
    if (isError(error)) {
      yield* put(ALERT_ERROR({ error }));
      yield* put(CATCH_ERROR({ error }));
    }
  }
};

export const planSideEffects = [
  takeEvery(actions.doLoadPlans, function* f(action) {
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const plans = sortBy(
        Object.values(yield* select(selectPlans)).filter(filterArrayEmpty),
        ['name'],
      );

      // Load request plan (by pin or plan) OR first plan OR empty page
      let loadPlanId: string | undefined;
      if (plans.length) {
        const requestPlanId = action.payload.pin?.planId;

        // Load plan by plan
        if (!loadPlanId && requestPlanId) {
          loadPlanId = plans.find((plan) => plan.id === requestPlanId)?.id;
        }

        // Re-open previous plan
        if (!loadPlanId) {
          const previousOpenPlanId = (yield* select(
            selectGeneric,
            GENERIC_OPEN_PLAN_ID(action.payload.context.assetId),
          )) as string | undefined;
          if (previousOpenPlanId) {
            loadPlanId = plans.find(
              (plan) => plan.id === previousOpenPlanId,
            )?.id;
          }
        }

        // Open first plan or no plan
        if (!loadPlanId) {
          loadPlanId = plans[0]?.id;
        }
      }

      if (loadPlanId) {
        yield* call(loadPlan, loadPlanId);
      } else {
        yield* put(
          NAVIGATE_MODAL({
            screen: ModalScreens.PLANS,
            params: { show: true },
          }),
        );
      }

      yield* put(HIDE_BLOCKING_LOADER());
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
        yield* put(CATCH_ERROR({ error }));
      }
    }
  }),

  takeEvery(actions.doPlansLoadPlan, function* f(action) {
    yield* call(loadPlan, action.payload.id);
  }),

  takeEvery(actions.doPlansCreatePlan, function* f(action) {
    try {
      const assetId = yield* select(selectPlansAssetId);
      const { id } = action.payload;
      yield* put(
        SET_ASSET_DELTA({
          delta: {
            plans: {
              [id]: {
                fileId: action.payload.fileId,
                id,
                name: action.payload.name,
                pinCount: 0,
              },
            },
          },
          id: assetId,
        }),
      );
      yield* put(NAVIGATE_MODAL_BACK());
      if (!(yield* select(selectPlansPlanId))) {
        yield delay(0);
        yield* put(actions.doPlansLoadPlan({ id }));
      }
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
        yield* put(CATCH_ERROR({ error }));
      }
    }
  }),

  takeEvery(actions.doPlansUpdatePlan, function* f(action) {
    try {
      const context = yield* select(selectPlansContext);
      const plan = yield* select(selectPlansPlan);
      if (!plan) {
        throw new Error('Plan not found');
      }
      if (context.context === AppContextEnum.remoteAssessment) {
        throw new Error('Online assessment not implemented');
      } else {
        yield* put(
          SET_ASSET_DELTA({
            delta: {
              plans: {
                [plan.id]: {
                  id: plan.id,
                  name: action.payload.name.trim() || plan.name,
                },
              },
            },
            id: context.assetId,
          }),
        );
      }
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
        yield* put(CATCH_ERROR({ error }));
      }
    }
  }),

  takeEvery(actions.doPlansUsePin, function* f() {
    try {
      const plan = yield* select(selectPlansPlan);
      if (!plan) {
        throw new Error('Plan not found');
      }

      const selectionKey = yield* select(selectSelectionKey);
      if (selectionKey) {
        const pin = yield* select(selectPlansPin);
        yield* put(actions.doClosePlans());
        // Use delay, this allows navigation to run successfully
        yield delay(0);
        yield* put(
          eventPinSelected({
            key: selectionKey,
            data: pin ? { planId: pin.planId, x: pin.x, y: pin.y } : null,
          }),
        );
        return;
      }

      const afterActionFind = yield* select(selectAfterActionFind);
      if (afterActionFind) {
        const selectedPinId = yield* select(selectPlansSelectedPinId);
        if (selectedPinId === undefined) {
          throw new Error("Pin hasn't been set");
        }
        const pin = (yield* select(selectPlansPlanPins)).find(
          (planPin) => planPin.id === selectedPinId,
        );
        if (pin === undefined) {
          throw new Error("Pin hasn't been set");
        }
        yield* put(actions.doClosePlans());
        // Use delay, this allows navigation to run successfully
        yield delay(0);
        yield* put({
          ...afterActionFind,
          payload: {
            ...afterActionFind.payload,
            id: selectedPinId,
          },
        });
        return;
      }

      throw new Error('No selection key set');
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
        yield* put(CATCH_ERROR({ error }));
      }
    }
  }),

  takeEvery(actions.doPlansDeletePlan, function* f(action) {
    try {
      const assetId = yield* select(selectPlansAssetId);
      if (!assetId) {
        throw new Error('Plan not found');
      }

      const plans = yield* select(selectPlans);
      const existingPlan = plans[action.payload.id];
      if (!existingPlan) {
        throw new Error('Plan not found');
      }

      // Open the first plan in the list that isn't this plan OR unload plan
      const nextPlan = sortBy(
        Object.values(yield* select(selectPlans)).filter(filterArrayEmpty),
        ['name'],
      ).find((plan) => plan.id !== action.payload.id);
      if (nextPlan) {
        yield* call(loadPlan, nextPlan.id);
      } else {
        yield* put(actions.doPlansSetPlan({ plan: undefined }));
      }

      yield* put(
        SET_ASSET_DELTA({
          delta: {
            plans: {
              [existingPlan.id]: null,
            },
          },
          id: assetId,
        }),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
        yield* put(CATCH_ERROR({ error }));
      }
    }
  }),

  takeEvery(actions.doClosePlans, function* f() {
    yield* put(NAVIGATE_MODAL_BACK());
  }),
];
